import { apiClient } from '@/lib/api-client';
import type { TourState } from '../types';

/**
 * Wire format returned by ``GET /api/v1/users/tour-states/`` and
 * ``POST /api/v1/users/tour-states/``. Mirrors
 * ``backend/users/serializers.py::UserTourStateSerializer``.
 *
 * We deliberately don't surface ``id`` / ``feature_id`` to the rest of the
 * frontend — only the fields that drive the tour state machine.
 */
interface TourStateWire {
  id: number;
  tour_id: string;
  version: number;
  completed: boolean;
  dismissed: boolean;
  last_step_index: number;
  feature_flag_key: string | null;
  feature_id: string | null;
  created_at: string;
  updated_at: string;
}

interface TourStateUpsertWire {
  tour_id: string;
  version: number;
  completed: boolean;
  dismissed: boolean;
  last_step_index: number;
  feature_flag_key: string | null;
}

function fromWire(wire: TourStateWire): TourState {
  return {
    tourId: wire.tour_id,
    version: wire.version,
    completed: wire.completed,
    dismissed: wire.dismissed,
    lastStepIndex: wire.last_step_index,
    updatedAt: wire.updated_at,
    featureFlagKey: wire.feature_flag_key,
  };
}

function toUpsertWire(state: TourState): TourStateUpsertWire {
  return {
    tour_id: state.tourId,
    version: state.version,
    completed: state.completed,
    dismissed: state.dismissed,
    last_step_index: state.lastStepIndex,
    feature_flag_key: state.featureFlagKey ?? null,
  };
}

const BASE = '/users/tour-states';

/**
 * Fetch every tour state for the authenticated user.
 *
 * Returns ``null`` on 401/403 so unauthenticated routes (``/login``, etc.)
 * don't bail out the TanStack Query — TourProvider mounts at the app
 * root and we don't want a noisy error there.
 *
 * ``null`` ("we don't know, the user wasn't authenticated") must stay
 * distinguishable from ``[]`` ("authenticated, genuinely no states"): the
 * provider treats ``null`` as not-hydrated and refetches after navigation,
 * otherwise a pre-login fetch would permanently mask the backend state and
 * re-prompt users for tours they already completed or dismissed.
 */
export async function getAllTourStates(): Promise<TourState[] | null> {
  try {
    const resp = await apiClient.get<TourStateWire[]>(`${BASE}/`);
    return resp.data.map(fromWire);
  } catch (err) {
    const status =
      typeof err === 'object' && err !== null && 'response' in err
        ? ((err as { response?: { status?: number } }).response?.status ?? null)
        : null;
    if (status === 401 || status === 403) return null;
    throw err;
  }
}

/** Upsert a single tour state (POST is idempotent on the backend). */
export async function upsertTourState(state: TourState): Promise<TourState> {
  const resp = await apiClient.post<TourStateWire>(`${BASE}/`, toUpsertWire(state));
  return fromWire(resp.data);
}

/** Clear a single tour's state (admin / "show me again" action). */
export async function deleteTourState(tourId: string, version: number): Promise<void> {
  const url = `${BASE}/${encodeURIComponent(tourId)}/?version=${version}`;
  await apiClient.delete(url);
}

/** Clear every tour state for the user. */
export async function clearAllTourStates(): Promise<void> {
  await apiClient.delete(`${BASE}/clear-all/`);
}
